import { Flex, Text } from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { sort } from "../../../utils/utils";
import { AcademicHierarchySelect } from "../../Selectors/AcademicHierarchySelect/HierarchySelector";
import { CourseGroupSelect } from "../../Selectors/CourseGroupSelect";
import { CourseLevelSelect } from "../../Selectors/CourseLevelSelect";
import { ProgramSelect } from "../../Selectors/ProgramSelect";
import { MULTI, SINGLE, Selector } from "../../Selectors/Selector";

export const CourseFilters = ({
  filters,
  dispatch,
  subjects,
  clear,
  setSelectedCourses,
  courseLevels,
}) => {
  const [hierarchySubjects, setHierarchySubjects] = useState(subjects);
  const [acLevelsWithSubjects, setAcLevelsWithSubjects] = useState([]);
  const [courseOptions, setCourseOptions] = useState([]);
  const [filteredCourseOptions, setFilteredCourseOptions] = useState([]);
  const [acSubClear, setAcSubClear] = useState(false);
  const [courseClear, setCourseClear] = useState(false);
  const [cgClear, setCgClear] = useState(false);
  const [selectedDeptId, setSelectedDeptId] = useState(null);

  const [coursesByProgram, setCoursesByProgram] = useState([]);
  const [selectedCourseGroups, setSelectedCourseGroups] = useState([]);

  useEffect(() => {
    setCourseClear(true);
    if (filters.courseLevels.length > 0) {
      const levels = courseLevels.filter((lvl) =>
        filters.courseLevels.includes(lvl.id)
      );
      const newCourseList = [];
      courseOptions.forEach((c) => {
        const courseLvl = c.code.match(/\d/g).join("");
        if (
          levels.find((lvl) => {
            const regex = new RegExp(`^${lvl.regexPattern}$`);
            return regex.test(courseLvl);
          })
        ) {
          newCourseList.push(c);
        }
      });
      setFilteredCourseOptions(newCourseList);
    } else {
      setFilteredCourseOptions(courseOptions);
    }
  }, [filters.courseLevels, courseOptions]);

  // Update unapproved counts on department selector if approval has been made
  useEffect(() => {
    if (subjects.length > 0) {
      setHierarchySubjects((prevHierarchySubjects) => {
        return prevHierarchySubjects.map((subject) => {
          const updatedSubject = subjects.find((s) => s.id === subject.id);
          if (
            updatedSubject &&
            updatedSubject.unapproved_outcome_count !== undefined
          ) {
            if (updatedSubject.unapproved_outcome_count === 0) {
              delete subject.unapproved_outcome_count;
              delete subject.renderAfterLabel;
            } else {
              subject.unapproved_outcome_count =
                updatedSubject.unapproved_outcome_count;
              subject.renderAfterLabel = updatedSubject.renderAfterLabel;
            }
          }
          return subject;
        });
      });
    }
  }, [subjects]);

  useEffect(() => {
    setHierarchySubjects(subjects);
    setCgClear(true);
  }, [clear]);

  useEffect(() => {
    if (acSubClear) {
      setAcSubClear(false);
      dispatch({ type: "UPDATE_FILTER", field: "subject", value: "" });
      setCgClear(true);
    }
  }, [acSubClear]);

  useEffect(() => {
    if (cgClear) {
      dispatch({ type: "UPDATE_FILTER", field: "courseGroups", value: [] });
      setCgClear(false);
      setCourseClear(true);
      setCourseOptions([]);
    }
  }, [cgClear]);

  useEffect(() => {
    if (courseClear) {
      setCourseClear(false);
    }
  }, [courseClear]);

  const handleHierarchySelect = (hierarchyId, options) => {
    const subjectList = [];
    let level;
    if (options) {
      level = options;
    } else {
      level = acLevelsWithSubjects.find((lvl) => lvl.id === hierarchyId);
    }
    level.children.forEach((lvl) => {
      if (lvl.label === "is academic subject") {
        const currentSubject = subjects.find((s) => s.id === lvl.id);
        if (currentSubject) {
          subjectList.push(currentSubject);
        }
      }
    });
    setAcSubClear(true);
    setHierarchySubjects(subjectList);
    setSelectedCourses([]);
  };

  const handleDeptSelect = (value) => {
    const dept = hierarchySubjects.find((s) => s.id === value);
    dispatch({ type: "UPDATE_FILTER", field: "subject", value: dept.title });
    dispatch({ type: "UPDATE_FILTER", field: "programs", value: [] });
    setCoursesByProgram([]);
    dispatch({ type: "UPDATE_FILTER", field: "courses", value: [] });
    setSelectedDeptId(dept.id);
    setSelectedCourses([]);
    setCgClear(true);
  };

  const handleCourseLevelSelect = (levels) => {
    setSelectedCourses([]);
    dispatch({ type: "UPDATE_FILTER", field: "courseLevels", value: levels });
    if (selectedCourseGroups.length > 0) {
      handleSetCourses(selectedCourseGroups, levels);
    } else if (coursesByProgram.length > 0) {
      let newCourseList = [];
      if (levels.length > 0) {
        const selectedLevels = courseLevels.filter((lvl) =>
          levels.includes(lvl.id)
        );
        coursesByProgram.forEach((c) => {
          const courseLvl = c.code.match(/\d/g).join("");
          if (
            selectedLevels.find((lvl) => {
              const regex = new RegExp(`^${lvl.regexPattern}$`);
              return regex.test(courseLvl);
            })
          ) {
            newCourseList.push(c.id);
          }
        });
      } else {
        newCourseList = coursesByProgram.map((c) => c.id);
      }

      dispatch({
        type: "UPDATE_FILTER",
        field: "courses",
        value: newCourseList,
      });
    }
  };

  const handleProgramSelect = (programIds) => {
    setSelectedCourses([]);
    setCgClear(true);
    dispatch({ type: "UPDATE_FILTER", field: "programs", value: programIds });
    if (programIds.length === 0) {
      setCoursesByProgram([]);
      dispatch({ type: "UPDATE_FILTER", field: "courses", value: [] });
    }
  };

  const handleCourseGroupSelect = (courseGroups) => {
    setSelectedCourses([]);
    setSelectedCourseGroups(courseGroups);
    dispatch({
      type: "UPDATE_FILTER",
      field: "courseGroups",
      value: courseGroups.map((cg) => cg.id),
    });
    let courses = [];
    courseGroups.forEach((cg) => {
      cg.courses.forEach((c) => {
        if (!courses.find((course) => course.id === c.id)) {
          c.label = c.code;
          courses.push(c);
        }
      });
    });
    courses = sort(courses, (course) => course.label);

    let newCourseList = [];
    if (filters.courseLevels.length > 0) {
      const levels = courseLevels.filter((lvl) =>
        filters.courseLevels.includes(lvl.id)
      );
      courses.forEach((c) => {
        const courseLvl = c.code.match(/\d/g).join("");
        if (
          levels.find((lvl) => {
            const regex = new RegExp(`^${lvl.regexPattern}$`);
            return regex.test(courseLvl);
          })
        ) {
          newCourseList.push(c.id);
        }
      });
    } else {
      newCourseList = courses.map((c) => c.id);
    }

    dispatch({ type: "UPDATE_FILTER", field: "courses", value: newCourseList });
    setCourseOptions(courses);
    setFilteredCourseOptions(courses);
    setCourseClear(true);
    if (courseGroups.length === 0) {
      handleSetCourses(courseGroups);
    }
  };

  const handleSelectedCourses = (courseIds) => {
    setSelectedCourses(courseIds);
    dispatch({ type: "UPDATE_FILTER", field: "courses", value: courseIds });
  };

  const departmentLabel = () => {
    const subjectsWithUnapprovedOutcomes = subjects.filter(
      (s) => !!s.renderAfterLabel
    );
    const totalUnapprovedCount = subjectsWithUnapprovedOutcomes.reduce(
      (total, s) => total + s.unapproved_outcome_count,
      0
    );

    return (
      <Text size="small">
        {t("curriculumMap.academicSubjectFilterLabel")}{" "}
        <Text size="small" fontStyle="italic">
          {`(${totalUnapprovedCount} ${t("dashboard.unapprovedOutcomes")})`}
        </Text>
      </Text>
    );
  };

  const handleSetCourses = (courseGroups, selectedLevels) => {
    if (courseGroups.length > 0) {
      const selectedCourseLevels = selectedLevels ?? filters.courseLevels;

      const courses = [];
      courseGroups.forEach((cg) => {
        cg.courses.forEach((c) => {
          if (!courses.find((courseId) => courseId === c.id)) {
            courses.push(c);
          }
        });
      });

      let newCourseList = [];
      if (selectedCourseLevels.length > 0) {
        const levels = courseLevels.filter((lvl) =>
          selectedCourseLevels.includes(lvl.id)
        );
        courses.forEach((c) => {
          const courseLvl = c.code.match(/\d/g).join("");
          if (
            levels.find((lvl) => {
              const regex = new RegExp(`^${lvl.regexPattern}$`);
              return regex.test(courseLvl);
            })
          ) {
            newCourseList.push(c.id);
          }
        });
      } else {
        newCourseList = courses.map((c) => c.id);
      }

      dispatch({
        type: "UPDATE_FILTER",
        field: "courses",
        value: newCourseList,
      });
    } else {
      dispatch({
        type: "UPDATE_FILTER",
        field: "courses",
        value:
          coursesByProgram.length > 0 ? coursesByProgram.map((c) => c.id) : [],
      });
    }
  };

  const handleCourseGroupsByProgram = (courseGroups) => {
    const courses = [];
    courseGroups.forEach((cg) => {
      cg.courses.forEach((c) => {
        if (!courses.find((courseId) => courseId === c.id)) {
          courses.push(c);
        }
      });
    });

    let newCourseList = [];
    if (filters.courseLevels.length > 0) {
      const levels = courseLevels.filter((lvl) =>
        filters.courseLevels.includes(lvl.id)
      );
      courses.forEach((c) => {
        const courseLvl = c.code.match(/\d/g).join("");
        if (
          levels.find((lvl) => {
            const regex = new RegExp(`^${lvl.regexPattern}$`);
            return regex.test(courseLvl);
          })
        ) {
          newCourseList.push(c);
        }
      });
    } else {
      newCourseList = courses;
    }

    setCoursesByProgram(newCourseList);
  };

  return (
    <Flex justifyItems="space-between" alignItems="start" wrap="wrap">
      <Flex.Item size="33%" margin="0 0 small 0">
        <AcademicHierarchySelect
          type={SINGLE}
          size="small"
          selectHandler={handleHierarchySelect}
          setAcLevelsWithSubjects={setAcLevelsWithSubjects}
          clear={clear}
        />
      </Flex.Item>
      <Flex.Item size="33%">
        <Selector
          type={SINGLE}
          size="small"
          label={departmentLabel()}
          options={hierarchySubjects}
          clear={acSubClear}
          selectHandler={handleDeptSelect}
          selectedItemId={
            hierarchySubjects.find((s) => s.title === filters.subject)?.id
          }
        />
      </Flex.Item>
      <Flex.Item size="33%">
        <CourseLevelSelect
          size="small"
          selectHandler={handleCourseLevelSelect}
          clear={clear}
        />
      </Flex.Item>
      <Flex.Item size="33%" margin="0 0 small 0">
        <ProgramSelect
          type={MULTI}
          label={t("assessment.programs")}
          selectHandler={handleProgramSelect}
          size="small"
          selectedPrograms={filters.programs}
          clear={clear}
          selectedSubjectId={selectedDeptId ?? subjects[0].id}
        />
      </Flex.Item>
      <Flex.Item size="33%">
        <CourseGroupSelect
          programIds={filters.programs}
          selectHandler={handleCourseGroupSelect}
          size="small"
          interaction={filters.programs.length > 0}
          clear={cgClear}
          handleSetCourses={handleSetCourses}
          handleCourseGroupsByProgram={handleCourseGroupsByProgram}
        />
      </Flex.Item>
      <Flex.Item size="33%">
        <Selector
          type={MULTI}
          id="course-filter"
          label={t("curriculumMap.courseFilterLabel")}
          options={filteredCourseOptions}
          selectHandler={handleSelectedCourses}
          size="small"
          clear={clear || courseClear}
          interaction={courseOptions.length > 0}
        />
      </Flex.Item>
    </Flex>
  );
};
